
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@supabase/auth-helpers-react";
import { BraceletAlerts } from "./bracelet/BraceletAlerts";
import { BraceletRegistrationDialog } from "./bracelet/BraceletRegistrationDialog";
import { RegisterButton } from "./bracelet/RegisterButton";
import { useToast } from "@/hooks/use-toast";
import { Button } from "./ui/button";
import { generateBraceletCode, parseReferenceCodes } from "@/utils/braceletUtils";

export function BraceletRegistration() {
  const [searchParams] = useSearchParams();
  const session = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [churchMode, setChurchMode] = useState(false);
  const [nickname, setNickname] = useState("");
  const [churchCode, setChurchCode] = useState("001");  // Set default church code to 001
  const [churchName, setChurchName] = useState("");
  const [registrationType, setRegistrationType] = useState("individual");
  const [quantity, setQuantity] = useState(1);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showRegisterButton, setShowRegisterButton] = useState(false);
  const [prefilledCodes, setPrefilledCodes] = useState<string[]>([]);
  const { toast } = useToast();
  
  const braceletId = searchParams.get("id");
  const isAdmin = searchParams.get("admin") === "true" || false;
  const prefilledCodesParam = searchParams.get("codes");

  useEffect(() => {
    if (prefilledCodesParam) {
      const codes = parseReferenceCodes(prefilledCodesParam);
      setPrefilledCodes(codes);
      
      if (isAdmin && session?.user && codes.length > 0) {
        setChurchMode(true);
        setRegistrationType("prefilled");
        setChurchCode("001");  // Always use church code 001
        setChurchName("TEST COMM");
        setOpen(true);
        setShowRegisterButton(true);
        
        // Automatically register bracelets for TEST COMM
        handleRegister();
      }
    } else if (isAdmin && session?.user) {
      setChurchMode(true);
      setShowRegisterButton(true);
    } else {
      checkBracelet();
    }
  }, [braceletId, session?.user, isAdmin, prefilledCodesParam]);

  const checkBracelet = async () => {
    setError(null);
    setSuccess(null);
    setShowRegisterButton(false);
    
    if (!braceletId) {
      console.log('No bracelet ID provided');
      return;
    }

    const cleanId = braceletId.replace(/^GHG/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    console.log('Checking bracelet with cleaned ID:', cleanId);

    try {
      const { data: bracelet, error: fetchError } = await supabase
        .from("bracelets")
        .select("*")
        .eq("referral_code", cleanId)
        .maybeSingle();

      if (fetchError) {
        console.error('Error checking bracelet:', fetchError);
        setError("Unable to verify code at this time. Please try again later.");
        return;
      }

      if (!bracelet) {
        console.log('No bracelet found - showing register button');
        if (session?.user) {
          setShowRegisterButton(true);
        }
        return;
      }

      if (!session?.user) {
        console.log('User not logged in');
        setSuccess("Welcome! Please sign in to register this product.");
        return;
      }

      if (bracelet.registered_user_id === session.user.id) {
        console.log('Product already registered to current user - staying silent');
        return;
      }

      if (bracelet.registered_user_id) {
        console.log('Product registered to different user');
        setError("This product has already been registered by another user.");
        return;
      }

      console.log('Product unregistered - showing registration dialog');
      setSuccess("Product found! Please enter a nickname to complete registration.");
      setOpen(true);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError("An unexpected error occurred. Please try again later.");
    }
  };

  const handleRegister = async () => {
    if (!session?.user) return;

    setIsRegistering(true);
    setError(null);

    try {
      if (churchMode) {
        await handleChurchRegistration();
      } else {
        await handleIndividualRegistration();
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsRegistering(false);
    }
  };

  const handleIndividualRegistration = async () => {
    if (!braceletId || !nickname.trim()) return;
    
    const cleanId = braceletId.replace(/^GHG/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    const { data: existingBracelet, error: checkError } = await supabase
      .from("bracelets")
      .select("*")
      .eq("referral_code", cleanId)
      .maybeSingle();

    if (checkError) {
      setError("Unable to verify product. Please try again.");
      return;
    }

    if (existingBracelet && existingBracelet.registered_user_id) {
      setError("This product has already been registered.");
      return;
    }
    
    // Determine church code based on special case bracelets
    let assignedChurchCode = null;
    if (['TEST001', 'TEST002', 'TEST003'].includes(cleanId)) {
      assignedChurchCode = "001"; // TEST COMM church code
    }

    if (existingBracelet) {
      const { error: updateError } = await supabase
        .from("bracelets")
        .update({
          registered_user_id: session?.user.id,
          registered_at: new Date().toISOString(),
          nickname: nickname.trim(),
          church_code: assignedChurchCode || existingBracelet.church_code
        })
        .eq("referral_code", cleanId);

      if (updateError) {
        setError("Unable to register product. Please try again.");
        return;
      }
    } else {
      const { error: insertError } = await supabase
        .from("bracelets")
        .insert({
          referral_code: cleanId,
          registered_user_id: session?.user.id,
          registered_at: new Date().toISOString(),
          nickname: nickname.trim(),
          created_by_user_id: session?.user.id,
          church_code: assignedChurchCode
        });

      if (insertError) {
        setError("Unable to register product. Please try again.");
        return;
      }
    }

    setSuccess("Your product has been successfully registered!");
    setOpen(false);
    setTimeout(() => navigate("/products"), 2000);
  };

  const handleChurchRegistration = async () => {
    // Check if this user is the test user by checking the profile name
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', session?.user?.id)
      .single();
      
    if (profileError) {
      console.error("Error fetching user profile:", profileError);
      setError("Unable to verify user permissions.");
      return;
    }
    
    if (profile?.full_name !== 'Test User') {
      setError("Only authorized administrators can register church bracelets.");
      return;
    }
    
    // Always set church code to 001 regardless of input
    const fixedChurchCode = "001";
    
    if (!churchName.trim()) {
      setError("Church name is required.");
      return;
    }

    const { data: existingChurch } = await supabase
      .from("church_branding")
      .select("*")
      .eq("church_code", fixedChurchCode)
      .maybeSingle();

    if (!existingChurch) {
      const { error: churchError } = await supabase
        .from("church_branding")
        .insert({
          church_code: fixedChurchCode,
          church_name: churchName.trim(),
          primary_color: "#0F4C81",
          secondary_color: "#1EAEDB"
        });

      if (churchError) {
        console.error("Error creating church:", churchError);
        setError("Unable to create church record. Please try again.");
        return;
      }
    }

    if (registrationType === "individual" && braceletId) {
      const cleanId = braceletId.replace(/^GHG/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      
      const { error: braceletError } = await supabase
        .from("bracelets")
        .upsert({
          referral_code: cleanId,
          church_code: fixedChurchCode,
          created_by_user_id: session?.user.id,
          created_at: new Date().toISOString()
        });

      if (braceletError) {
        console.error("Error registering bracelet:", braceletError);
        setError("Unable to register bracelet. Please try again.");
        return;
      }
    } else if (registrationType === "prefilled" && prefilledCodes.length > 0) {
      // For TEST001, TEST002, TEST003
      const authorizedCodes = ['TEST001', 'TEST002', 'TEST003'];
      const bracelets = prefilledCodes
        .filter(code => authorizedCodes.includes(code))
        .map(code => ({
          referral_code: code,
          church_code: fixedChurchCode,
          created_by_user_id: session?.user.id,
          created_at: new Date().toISOString()
        }));
      
      if (bracelets.length === 0) {
        setError("No authorized bracelet codes provided. Only TEST001, TEST002, TEST003 can be registered to TEST COMM.");
        return;
      }
      
      const { error: bulkError } = await supabase
        .from("bracelets")
        .upsert(bracelets);
        
      if (bulkError) {
        console.error("Error registering prefilled bracelets:", bulkError);
        setError("Unable to register bracelets with prefilled codes. Please try again.");
        return;
      }
    } else if (registrationType === "bulk") {
      setError("Bulk registration is not supported for TEST COMM. Please use specific codes TEST001, TEST002, or TEST003.");
      return;
    }

    setSuccess(`Successfully registered bracelets for ${churchName}!`);
    setOpen(false);
    setTimeout(() => navigate("/products"), 2000);
  };

  const openChurchRegistration = () => {
    setChurchMode(true);
    setOpen(true);
  };

  const openIndividualRegistration = () => {
    setChurchMode(false);
    setOpen(true);
  };

  return (
    <>
      <BraceletAlerts error={error} success={success} session={session} />
      {showRegisterButton && session?.user && (
        <div className="flex flex-col gap-3 items-center">
          {isAdmin && (
            <Button 
              variant="default" 
              className="max-w-[250px] bg-red-600 hover:bg-red-700 text-white h-10 text-sm font-medium rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
              onClick={openChurchRegistration}
            >
              Register Church Bracelets
            </Button>
          )}
          <RegisterButton onClick={openIndividualRegistration} />
        </div>
      )}
      <BraceletRegistrationDialog
        open={open}
        setOpen={setOpen}
        nickname={nickname}
        setNickname={setNickname}
        handleRegister={handleRegister}
        isRegistering={isRegistering}
        churchMode={churchMode}
        churchCode={churchCode}
        setChurchCode={setChurchCode}
        churchName={churchName}
        setChurchName={setChurchName}
        registrationType={registrationType}
        setRegistrationType={setRegistrationType}
        quantity={quantity}
        setQuantity={setQuantity}
        prefilledCodes={prefilledCodes}
      />
    </>
  );
}
